import React, { useMemo } from 'react';
import { FeaturesEnum } from '@api/ringil-api.v2.tsx';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgDelete from '@icons/delete-trash.svg?react';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import SvgTruck from '@icons/truck.svg?react';
import CarrierDistributionSelector, {
  CarrierDistributionSelectorData,
  DistributionType,
  distributionTypesForShipment,
} from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { isLadenFactorEligibleFromLocations } from '@utils/expeditionUtils.ts';
import { isNullOrUndefined } from '@utils/utils.tsx';
import { Form } from '@hooks/useForm/useForm.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { unitsByMuFromLocations } from '@app/pages/expedition/create/contractedUnits.ts';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  /** The distribution the form was pre-filled with, so the selector can show what changed. */
  originalDistribution: CarrierDistributionSelectorData;
  onCreateDistribution: () => void;
  hasFeature: (features: FeaturesEnum[]) => boolean;
  /** Backend validation messages scoped to the distribution. */
  backendValidation: React.ReactNode;
}

/** The shipment snapshot the API carriers (Dachser, Schenker, DHL) validate against. */
const buildExpeditionValidations = (form: Form<NewExpeditionForm>) => {
  const locations = form.values.locations;

  return [
    {
      locations: locations
        .filter(
          loc =>
            (loc.location?.addressTemplate?.country && loc.location?.addressTemplate?.postalCode) ||
            (loc.manualAddress?.countryCode3 && loc.manualAddress?.postalCode),
        )
        .map(loc => ({
          id: loc.id,
          companyLocationId: loc.companyLocationId,
          city: loc.location?.addressTemplate?.city || loc.manualAddress?.city,
          country: loc.location?.addressTemplate?.country || loc.manualAddress?.countryCode3,
          postalCode: loc.location?.addressTemplate?.postalCode || loc.manualAddress?.postalCode,
          street: loc.location?.addressTemplate?.streetNr || loc.manualAddress?.streetNr,
          orders: loc.orders
            ?.filter(order => !loc.unloadingOrderIds?.includes(order.id))
            .map(order => ({
              items: order.items?.map(item => ({
                length: item.length,
                width: item.width,
                height: item.height,
                weight: item.weight,
                amount: item.amount,
              })),
              loadingLocationId: loc.id,
              unloadingLocationId: locations?.filter(l => l.unloadingOrderIds?.includes(order.id))[0]?.id,
              totalWeight: order.orderTotalWeight,
              orderValue: order.orderValue?.price,
              orderCurrency: order.orderValue?.currency,
            })),
          contactEmail: loc.localityMode === LocalityEntryModeEnum.MANUALLY ? loc.manualAddress?.mainContactEmail : loc.selectedPerson?.contactEmail,
          contactPhone: loc.localityMode === LocalityEntryModeEnum.MANUALLY ? loc.manualAddress?.mainContactPhone : loc.selectedPerson?.contactPhone,
          contactName: loc.localityMode === LocalityEntryModeEnum.MANUALLY ? loc.manualAddress?.mainContactPerson : loc.selectedPerson?.contactName,
          deliverySlot: loc.timeslots?.dayWithInterval
            ? {
              days: loc.timeslots.dayWithInterval,
            }
            : null,
        })),
      cargoType: form.values.cargoType?.cargoTypeCode
        ? {
          cargoTypeCode: form.values.cargoType.cargoTypeCode,
        }
        : null,
      incotermsValue: form.values.incoterms?.incotermsValue,
      incotermsDescription: form.values.incoterms?.incotermsDescription,
      huEkaerNumber: form.values.huEkaerNumber,
      roUitNumber: form.values.roUitNumber,
      utnCode: form.values.utnCode,
      polandSentCarrierUpdateKey: form.values.polandSent?.sentCarrierUpdateKey,
      polandSentNumber: form.values.polandSent?.sentNumber,
      attachments: form.values.attachments?.map(attachment => ({
        documentType: attachment.documentType,
        contentType: attachment.contentType,
      })),
    },
  ];
};

const getTotalHandlingUnits = (locations: ExpeditionLocality[]) => {
  if (!locations) return 0;
  return locations
    .flatMap(loc => loc.orders || [])
    .flatMap(order => order.items || [])
    .reduce((sum, item) => sum + (item.amount || 0), 0);
};

/**
 * How the shipment reaches a carrier: spot demand, fixed price or direct assignment.
 *
 * The selector is also given a snapshot of the shipment so it can run the API carriers'
 * (Dachser, Schenker, DHL) own validation before anything is sent.
 */
const CarrierSection: React.FC<ComponentProps> = ({ form, originalDistribution, onCreateDistribution, hasFeature, backendValidation }) => {
  const { tr } = useTranslationLgs();
  const locations = form.values.locations;
  const lastLocation = locations[locations.length - 1];

  // `CarrierDistributionSelector` debounces this prop by reference and re-runs the carrier
  // validation call whenever the reference changes. Built inline it would be a new array on every
  // render, firing a request 500 ms after any keystroke anywhere on the form — so the reference is
  // held stable until the content it is derived from actually differs.
  const builtValidations = buildExpeditionValidations(form);
  const validationsKey = JSON.stringify(builtValidations);
  const expeditionValidations = useMemo(() => builtValidations, [validationsKey]);
  const contractedUnitsByMu = unitsByMuFromLocations(locations);

  if (isNullOrUndefined(form.values.distribution)) {
    return (
      <RowButton
        title={tr('CreateExpedition.selectOrAssignCarrierSectionButton', 'Select or assign carrier')}
        icon={<SvgTruck width={32} height={32} className={'text-content-quaternary'} />}
        onClick={onCreateDistribution}
        dataTestId={'carrierSelection'}
      />
    );
  }

  return (
    <Card>
      <div className={'flex flex-row items-center justify-between'}>
        <COHeadline variant={'h3'} title={tr('CreateExpedition.carrierSectionTitle', 'Carrier')} />
        <RingilButton
          mode={'table'}
          icon={<SvgDelete />}
          tooltip={tr('CreateExpedition.removeCarrierTooltip', 'Remove the carrier selection')}
          onClick={() => form.setFieldValue(form.names.distribution, null)}
        />
      </div>
      {backendValidation}
      <CarrierDistributionSelector
        allowedDistributionTypes={distributionTypesForShipment(!!form.values.tmplV2Id, [DistributionType.SPOT, DistributionType.FIXED, DistributionType.ASSIGN])}
        dataTestIdPrefix={'carrierSelection'}
        pickupTimezoneId={locations[0]?.location?.timezoneId || locations[0]?.manualAddress?.timezoneId}
        distribution={form.values.distribution}
        originalDistribution={originalDistribution}
        onChange={v => {
          form.setFieldValue(form.names.distribution, v);
        }}
        validationErrors={form.validationErrors.filter(e => e.path?.startsWith('distribution')).map(e => ({ path: e.path || '', message: e.message }))}
        aiCarrierAutoSelectionDto={{
          loadingLocationId: locations[0]?.location?.locationId,
          unloadingLocationId: lastLocation?.location?.locationId,
          loadingCity: locations[0]?.manualAddress?.city,
          loadingCountry: locations[0]?.manualAddress?.countryCode3,
          unloadingCity: lastLocation?.manualAddress?.city,
          unloadingCountry: lastLocation?.manualAddress?.countryCode3,
          weight: locations[0]?.orders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.weight || 0), 0) || 0), 0) || 0,
          countPallets:
            locations[0]?.orders?.reduce((sum, order) => sum + (order.items?.reduce?.((itemSum, item) => itemSum + (item.amount || 0), 0) || 0), 0) || 0,
          cargoAmount: locations[0]?.orders?.reduce((sum, order) => sum + (order.orderAmount || 0), 0) || 0,
        }}
        ladenFactorEligible={isLadenFactorEligibleFromLocations(locations, hasFeature([FeaturesEnum.LADEN_FACTOR]))}
        handlingUnitCount={getTotalHandlingUnits(locations)}
        defaultLadenFactor={form.values.defaultLadenFactor}
        expeditionValidations={expeditionValidations}
        contractedUnitsByMu={contractedUnitsByMu}
        contractedInfoText={
          form.values.tmplV2Name
            ? tr('CreateExpedition.contractedLineInfoText', 'Změny platí jen pro tuto přepravu. Linka {{name}} zůstane beze změny.', { name: form.values.tmplV2Name })
            : undefined
        }
        contractedSubjectName={form.values.tmplV2Name}
      />
    </Card>
  );
};

export default CarrierSection;